import { API, ApiPrefixes } from "../api";
import { setUserData } from "../auth";

export const staffApi = API.injectEndpoints({
  endpoints: (builder) => ({
    getStaffList: builder.query({
      query: (page) => ({
        url:
          ApiPrefixes["staffManagement"] + `?guard=staff&page=${page}&limit=20`,
      }),
      providesTags: (result = {}) => {
        if (!result?.staffs) return [{ type: "Staff", id: "LIST" }];
        return [
          { type: "Staff", id: "LIST" },
          ...result?.staffs?.map(({ id }) => ({ type: "Staff", id: id })),
        ];
      },
      transformResponse: (responseData) => {
        const { staffs = [], pagination = {} } = responseData.data;
        return {
          staffs: staffs,
          pagination,
        };
      },
    }),

    searchStaffList: builder.query({
      query: ({ search, page }) => ({
        url:
          ApiPrefixes["staffManagement"] +
          `/search/all?guard=staff&page=${page}&limit=20&search=${search}`,
      }),
      providesTags: (result = {}) => {
        if (!result.staffs) return [{ type: "Staff", id: "LIST" }];
        return [
          { type: "Staff", id: "LIST" },
          ...result?.staffs?.map(({ id }) => ({ type: "Staff", id: id })),
        ];
      },
      transformResponse: (responseData) => {
        return responseData.data;
      },
    }),

    getSingleStaff: builder.query({
      query: (staffId) => ({
        url: ApiPrefixes["staffManagement"] + `/${staffId}/view?guard=staff`,
      }),
      transformResponse: (responseData) => responseData.data,
      providesTags: (result, error, arg) => [
        { type: "Staff", id: arg },
        "single-staff",
      ],
    }),

    createStaff: builder.mutation({
      query: (body) => ({
        url:
          ApiPrefixes["staffManagement"] +
          `/create?guard=staff&page=1&limit=20`,

        method: "POST",
        body: body,
      }),
      invalidatesTags: (result, err, arg) => [{ type: "Staff", id: arg }],
    }),

    suspendStaff: builder.mutation({
      query: (credential) => ({
        url:
          ApiPrefixes["staffManagement"] +
          `/${credential.staffId}/suspend?guard=staff`,
        method: "PUT",
        body: credential.payload,
      }),
      invalidatesTags: (result, err, arg) => [{ type: "Staff", id: arg?.id }],
    }),

    unSuspendStaff: builder.mutation({
      query: (staffId) => ({
        url:
          ApiPrefixes["staffManagement"] + `/${staffId}/unsuspend?guard=staff`,
        method: "PUT",
      }),
      invalidatesTags: (result, err, arg) => [{ type: "Staff", id: arg }],
    }),

    deleteStaff: builder.mutation({
      query: (credential) => ({
        url:
          ApiPrefixes["staffManagement"] +
          `/${credential.staffId}/delete?guard=staff`,
        method: "DELETE",
        body: credential.payload,
      }),
      invalidatesTags: (result, err, arg) => [{ type: "Staff", id: arg }],
    }),

    assignBranch: builder.mutation({
      query: (credential) => ({
        url:
          ApiPrefixes["staffManagement"] +
          `/${credential.staffId}/assign?guard=staff`,
        method: "PATCH",
        body: credential.payload,
      }),
      invalidatesTags: (result, err, arg) => [
        { type: "Staff", id: arg },
        "single-staff",
      ],
    }),

    changeBranch: builder.mutation({
      query: (credential) => ({
        url:
          ApiPrefixes["staffManagement"] +
          `/${credential.staffId}/change_branch?guard=staff`,
        method: "PATCH",
        body: credential.payload,
      }),
      invalidatesTags: (result, err, arg) => [{ type: "Staff", id: arg }],
    }),

    updateBranch: builder.mutation({
      query: (credential) => ({
        url:
          ApiPrefixes["staffManagement"] +
          `/${credential.staffId}/update?guard=staff`,
        method: "PATCH",
        body: credential.payload,
      }),
      invalidatesTags: (result, err, arg) => [{ type: "Staff", id: arg?.id }],
    }),

    updateStaff: builder.mutation({
      query: (credential) => ({
        url:
          ApiPrefixes["staffManagement"] +
          `/${credential.staffId}/update?guard=staff`,
        method: "POST",
        body: credential.payload,
      }),
      invalidatesTags: (result, err, arg) => [
        { type: "Staff", id: arg?.staffId },
        "single-staff",
      ],
    }),

    getCurrentStaffProfile: builder.query({
      query: () => ({
        url: `cooperatives/profile?guard=staff`,
      }),
      providesTags: [{ type: "Staff", id: "CURRENT_PROFILE" }],
      transformResponse: (responseData) => responseData.data,
    }),

    switchBranch: builder.mutation({
      query: (branchId) => ({
        url: `cooperatives/profile/${branchId}/switch_branch?guard=staff`,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "Staff", id: "CURRENT_PROFILE" },
        { type: "Staff", id: "LIST" },
        "StaffDashboard",
      ],
      async onQueryStarted(_branchId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          // Fresh profile for the new active_branch_id before wiping caches
          const profile = await dispatch(
            API.endpoints.getCurrentStaffProfile.initiate(undefined, {
              forceRefetch: true,
            })
          ).unwrap();

          dispatch(setUserData(profile));
          // Drop all branch-scoped query caches so mounted pages refetch
          dispatch(API.util.resetApiState());
          // Keep profile/header stable after the reset
          dispatch(
            API.util.upsertQueryData(
              "getCurrentStaffProfile",
              undefined,
              profile
            )
          );
        } catch {
          // Switch failed or profile refresh failed; leave existing cache as-is
        }
      },
    }),

    removeBranchFromStaff: builder.mutation({
      query: ({ staffId, branchId }) => ({
        url: `cooperatives/staff_managements/${staffId}/branches/${branchId}?guard=staff`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Staff", id: "LIST" }, "single-staff"],
    }),

    downloadStaffCSV: builder.mutation({
      query: ({ search, page }) => ({
        url:
          ApiPrefixes["staffManagement"] +
          `/download/csv?guard=staff&page=${page || 1}&limit=1000&search=${
            search || ""
          }`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }),

    downloadStaffPDF: builder.mutation({
      query: ({ search, page }) => ({
        url:
          ApiPrefixes["staffManagement"] +
          `/download/pdf?guard=staff&page=${page || 1}&limit=1000&search=${
            search || ""
          }`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useGetStaffListQuery,
  useSearchStaffListQuery,
  useGetSingleStaffQuery,
  useCreateStaffMutation,
  useSuspendStaffMutation,
  useUnSuspendStaffMutation,
  useDeleteStaffMutation,
  useAssignBranchMutation,
  useChangeBranchMutation,
  useUpdateBranchMutation,
  useUpdateStaffMutation,
  useGetCurrentStaffProfileQuery,
  useSwitchBranchMutation,
  useRemoveBranchFromStaffMutation,
  useDownloadStaffCSVMutation,
  useDownloadStaffPDFMutation,
} = staffApi;
